import * as React from 'react'
import { useCallback, useRef, useState, useEffect, forwardRef } from 'react'
import { TrackUser, TrackEvent, PersonalInfo } from '../../helpers/TrackingHelper'
import { navigate } from 'gatsby'
import traverse from './helpers/traverse'
import validateInputs from './helpers/validateInputs'
import getUTMCodes from './helpers/getUTMCodes'
import uploadWithProgress from './helpers/uploadWithProgress'
import extractFilesAndForm from './helpers/extractFilesAndForm'
import PropTypes from 'prop-types'
import FormSubmittedAnim from './FormSubmittedAnim'
import mergeRefs from '../../helpers/mergeRefs'
import isNotEmpty from './validators/isNotEmpty'
import isPhone from './validators/isPhone'
import isEmail from './validators/isEmail'
import cascade from './validators/cascade'
import GoogleAds from '../../../utils/GoogleAds'

export const FormContext = React.createContext(false)

const FormWrapper = forwardRef(
  (
      {
        children,
        onSubmit,
        action,
        name,
        form,
        className = '',
        formClassName = '',
        encType = 'multipart/form-data',
        method = 'POST',
        minimumTimeout = 750,
        saveOnSubmit = true,
        onValidationError = false,
        onSubmitError = false,
        useSubmitAnimation = true,
        conversionEventName = 'form_submission',
      },
      forwardedRef
  ) => {
    const formElement = useRef()
    const [submitted, setSubmitted] = useState(false)
    const [status, setStatus] = useState({
      isWorking: false,
      isPlaying: false,
      progress: 0,
      label: '',
    })
    const scrollToForm = useCallback(() => {
      formElement.current?.scrollIntoView()
    }, [])

    const [person, setPerson] = useState({
      name: '',
      email: '',
      phone: '',
    })

    useEffect(() => {
      // This Effect extracts user personal info from the form
      // as it's being filled out
      const { formData } = extractFilesAndForm(form, name || '')
      const _person = PersonalInfo(formData)
      setPerson({ name: _person.name, email: _person.email, phone: _person.phone })
    }, [form, name, extractFilesAndForm, PersonalInfo, setPerson])

    useEffect(() => {
      // This Effect starts tracking user, as soon as we have some data to track 
      // it sends the form_started event
      const nameCorrect = isNotEmpty(person.name)
      const emailCorrect = cascade(isNotEmpty, isEmail)(person.email)
      const phoneCorrect = cascade(isNotEmpty, isPhone)(person.phone)
      const throttle = 2500
      // require name and (phone or email)
      if (!nameCorrect.isValid || (!emailCorrect.isValid && !phoneCorrect.isValid)) {
        return
      }
      const timeout = setTimeout(() => {
        TrackUser(person.email, {
          name: person.name,
          phone: person.phone,
          email: person.email,
        })
        TrackEvent(
          'form_started',
          {
            category: 'Form Fill',
            label: name,
            form: person,
          },
          {
            klaviyoEventName: 'Form Started',
          }
        )
      }, throttle)
      return () => clearTimeout(timeout)
    }, [person, person.name, person.email, person.phone, name])

    useEffect(() => {
      // This effect is triggered when the form is submitted
      // to navigate to the next page once submission and animation is done
      if (status.isWorking || status.isPlaying || status.progress < 1) return
      if (typeof action === 'function') {
        console.log('Form submission Effect [fn]', status, action)
        action({ success: true })
        return
      }
      console.log('Form submission Effect [fn]', status, action)
      // console.log('Form submission Effect', status, action)
      TrackEvent('form_thank_you_redirect', 
        { 
          category: 'Thank You Page', 
          label: name,
        }, 
        {
          klaviyoEventName: 'Form Thank You Redirect',
        })
      navigate(action)
    }, [status.isWorking, status.isPlaying, status.progress])

    const TrackFormSubmission = useCallback(
      formData => {
        let googleAdsConversion = ''
        if (GoogleAds.Conversions[conversionEventName]) {
          googleAdsConversion = GoogleAds.Conversions[conversionEventName]
        } else {
          googleAdsConversion = GoogleAds.Conversions.form_submission
        }
        TrackUser(formData.email, PersonalInfo(formData))
        if (conversionEventName !== 'form_submission') {
          TrackEvent(
            conversionEventName,
            {
              source: 'Form',
              formId: name,
              formTitle: name,
              email: formData.email,
            },
            {
              klaviyoEventName: name,
            }
          )
        }
        TrackEvent(
          'form_submission',
          { label: name, formId: name, formTitle: name, form: formData },
          {
            googleAdsConversion,
            klaviyoEventName: 'Form Submission',
          }
        )
      },
      [name, conversionEventName]
    )

    const handleSubmit = useCallback(
      // TODO: improve code, cognitive complexity is too high
      async e => {
        e.preventDefault()
        setSubmitted(true)
        // check errors - traverse the form
        const inputs = traverse(children)
        const errors = validateInputs(inputs, form)
        if (errors.length > 0) {
          const error_message =
            'Please fill out all required fields.\n' +
            errors[0].error +
            (errors.length > 1 ? `\r... and ${errors.length - 1} more error(s)` : '')
          console.log(error_message)

          if (onValidationError && typeof onValidationError === 'function') {
            onValidationError(error_message, errors)
          } else {
            window?.alert(error_message)
          }
          return
        }

        try {
          setTimeout(() => setStatus(s => ({ ...s, isPlaying: false })), minimumTimeout)
          setStatus(s => ({ ...s, isWorking: true, isPlaying: true, label: 'Sending form' }))
          if (onSubmit && typeof onSubmit === 'function') {
            await Promise.resolve(onSubmit({ ...e, form }))
          }
          if (!saveOnSubmit) {
            setStatus(st => {
              return {
                ...st,
                isWorking: false,
                label: 'Sending Form Header',
                progress: 1,
              }
            })
            return
          }
          setStatus(s => ({ ...s, isWorking: true, label: 'Sending form header' }))
          scrollToForm()
          const { formData, files } = extractFilesAndForm(form, name)
          const totalSize = files.reduce((c, f) => c + f.file.size, 0)
          TrackFormSubmission(formData)
          const query = getUTMCodes()
          let info = await fetch('/api/send-form' + query, {
            method: 'POST',
            body: JSON.stringify({
              ...formData,
              _formName: name,
              _qs: window?.qs,
              _initial_page: window?._initial_page,
            }),
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          })
          let json = await info.json()
          let totalSent = 0
          const updateProgress = ev => {
            let progress = (totalSent + ev.current) / totalSize
            setStatus(st => ({ ...st, progress }))
          }
          for (let { file, key, index } of files) {
            setStatus(st => ({
              ...st,
              isWorking: true,
              label: `Uploading file ${file.name}. Total size: ${totalSize}`,
            }))
            try {
              let qs = `file=${encodeURIComponent(file.name)}&form=${encodeURIComponent(
                json.id
              )}&key=${encodeURIComponent(key)}&i=${encodeURIComponent(index)}&_formName=${name}`
              await uploadWithProgress(`/api/send-form?${qs}`, file, updateProgress)
            } catch (err) {
              console.log('Form submission error')
              console.error(err)
              TrackEvent(
                'form_submission_error',
                {
                  message: e.message,
                },
                {
                  klaviyoEventName: 'Form Submission Error',
                }
              )
            }
            totalSent += file.size
          }
          setStatus(st => ({ ...st, progress: 1, label: 'Submitting' }))
          /* consider reading info = */
          await fetch('/api/send-form', {
            method: 'POST',
            body: JSON.stringify({ id: json.id, finished: true, _formName: name }),
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          })
          setStatus(st => {
            return { ...st, isWorking: false }
          })
        } catch (err) {
          console.error(err.message)
          TrackEvent('Form Submission Error', 
            { message: err.message }
          )
          if (onSubmitError && typeof onSubmitError === 'function') {
            onSubmitError(err)
          }
        }
      },
      [
        form,
        name,
        children,
        extractFilesAndForm,
        traverse,
        validateInputs,
        scrollToForm,
        minimumTimeout,
        TrackFormSubmission
      ]
    )

    // TODO: Greg where should these go?
    // const [formSubmitted, setFormSubmitted] = React.useState(false)
    // const [clicked, setClicked] = React.useState(false)
    // const [progress, setProgress] = React.useState(0)
    // React.useEffect(() => {
    //   if (!clicked) return
    //   const interval = setInterval(() => {
    //     setProgress(prev => {
    //       if (prev < 1) {
    //         return prev + 0.01
    //       } else {
    //         // return 0
    //         clearInterval(interval)
    //         setFormSubmitted(true)
    //         return 1
    //       }
    //     })
    //   }, 50)
    //   return () => clearInterval(interval)
    // }, [clicked, setProgress, setFormSubmitted])

    return (
      <FormContext.Provider value={submitted}>
        <form
          className={formClassName + ' relative '}
          name={name}
          data-netlify={'true'}
          method={method}
          action={typeof action === 'function' ? '?send' : action}
          encType={encType}
          onSubmit={handleSubmit}
          onReset={() => setSubmitted(false)}
          ref={mergeRefs(formElement, forwardedRef)}
        >
          {(status.isWorking || status.isPlaying) && useSubmitAnimation ? (
            <FormSubmittedAnim />
          ) : null}
          <div className={className}>{children}</div>
        </form>
      </FormContext.Provider>
    )
  }
)
FormWrapper.displayName = 'FormWrapper'
FormWrapper.propTypes = {
  // Required props
  action: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  onSubmit: PropTypes.func,
  onValidationError: PropTypes.func,
  onSubmitError: PropTypes.func,
  name: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  // Optional props
  method: PropTypes.string,
  encType: PropTypes.string,
  className: PropTypes.string,
  minimumTimeout: PropTypes.number,
  saveOnSubmit: PropTypes.bool,
  formClassName: PropTypes.string,
  useSubmitAnimation: PropTypes.bool,
  conversionEventName: PropTypes.string,
}

export default FormWrapper
